import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ServiceBox from "../serviceslist/ServiceBox";
import { Avatar, Chip } from "@mui/material";
import useCurrentUser from "../../user/useCurrentUser";
import FilterHeader from "./FilterHeader";
import StaffFilterInput from "./StaffFilterInput";
import PendingPaymentIndicator from "./PendingPaymentIndicator";
import { getPendingPayments } from "./getPendingPayments";
import { useServices } from "@/hooks/useServices";
import "./TaskManager.scss";

const PENDING_FLASH_MS = 1200;

function collectStaffSearchText(record) {
  const parts = [record?.name, record?.status, record?.notify];

  for (let i = 1; i <= 20; i += 1) {
    const task = record?.[`t${i}`];
    if (!task || typeof task !== "object") continue;
    parts.push(
      task.serTitle,
      task.serDescription,
      task.serNote,
      task.serStation
    );
  }

  return parts
    .filter((part) => part != null && String(part).trim() !== "")
    .join(" ")
    .toLowerCase();
}

const TaskManager = ({
  taskRecord,
  onCellClick,
  activeCell,
  activeName,
  headerEnd = null,
  showPendingPayment = true,
}) => {
  const [activeRow, setActiveRow] = useState(null);
  const [userRole, setUserRole] = useState([]);
  const [staffFilter, setStaffFilter] = useState("");
  const [flashKey, setFlashKey] = useState(null);
  const cellRefs = useRef(new Map());
  const flashTimerRef = useRef(null);

  const allowedRoles = ["Manager", "Owner"];
  const { currentUser, loading } = useCurrentUser();
  const { services: catalogServices } = useServices();

  useEffect(() => {
    if (currentUser && currentUser.empRole) {
      setUserRole(currentUser.empRole);
    }
  }, [currentUser]);

  const isTooltipDisabled = () =>
    !userRole.some((role) => allowedRoles.includes(role));

  const filteredRows = useMemo(() => {
    const query = staffFilter.trim().toLowerCase();
    const rows = taskRecord.map((record, index) => ({ record, index }));
    if (!query) return rows;
    return rows.filter(({ record }) =>
      collectStaffSearchText(record).includes(query)
    );
  }, [taskRecord, staffFilter]);

  const pendingPayments = useMemo(
    () =>
      showPendingPayment
        ? getPendingPayments(taskRecord, catalogServices)
        : [],
    [showPendingPayment, taskRecord, catalogServices]
  );

  const setCellRef = (recordIndex, taskKey, node) => {
    const key = `${recordIndex}:${taskKey}`;
    if (node) cellRefs.current.set(key, node);
    else cellRefs.current.delete(key);
  };

  useEffect(() => {
    return () => {
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    };
  }, []);

  const handleCellClick = (recordIndex, taskKey) => {
    const selectedTask = taskRecord[recordIndex][taskKey];
    onCellClick(recordIndex, taskKey, selectedTask);
    setActiveRow(recordIndex);
  };

  const handlePendingSelect = (item) => {
    handleCellClick(item.recordIndex, item.taskKey);
    const key = `${item.recordIndex}:${item.taskKey}`;
    const node = cellRefs.current.get(key);
    node?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
    setFlashKey(key);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => {
      setFlashKey(null);
      flashTimerRef.current = null;
    }, PENDING_FLASH_MS);
  };

  useEffect(() => {
    if (activeName) {
      const index = taskRecord.findIndex(
        (record) => record.name === activeName
      );
      setActiveRow(index);
    }
  }, [activeName, taskRecord]);

  const renderStatusOrAvatar = (record) => {
    const status = record.status;
    if (status.includes("working")) {
      return (
        <span className="status-busy">
          Busy <AccessTimeOutlinedIcon className="status-icon" />
        </span>
      );
    }
    if (status.includes("break")) {
      return (
        <span className="status-break">
          Break <AccessTimeOutlinedIcon className="status-icon" />
        </span>
      );
    }
    return (
      <Avatar
        src={record.avatar}
        className="avatar"
        variant="rounded"
        alt="Avatar"
      />
    );
  };

  const renderServiceBoxes = (task, recordIndex) => {
    return [...Array(20).keys()].map((i) => {
      const taskKey = `t${i + 1}`;
      const cellKey = `${recordIndex}:${taskKey}`;
      const isActive =
        activeCell?.recordIndex === recordIndex &&
        activeCell?.taskKey === taskKey;
      const isFlash = flashKey === cellKey;

      const updatedTask = { ...task[taskKey] };
      if (updatedTask.serTitle === "RESET") {
        updatedTask.serTitle = "";
        updatedTask.serDescription = "";
        updatedTask.serAvatar.color1 = "transparent";
        updatedTask.serAvatar.color2 = "transparent";
      }

      return (
        <td key={taskKey} className="service-cell">
          <div
            ref={(node) => setCellRef(recordIndex, taskKey, node)}
            className={`service-box ${isActive ? "taskcellClick" : ""} ${isFlash ? "taskcellFlash" : ""}`.trim()}
            onClick={() => handleCellClick(recordIndex, taskKey)}
          >
            <ServiceBox
              avatar={updatedTask.serAvatar}
              title={updatedTask.serTitle}
              description={updatedTask.serDescription}
              amount={updatedTask.serAmount}
              supply={updatedTask.serSupply}
              station={updatedTask.serStation}
              note={updatedTask.serNote}
              disableTooltip={isTooltipDisabled()}
            />
          </div>
        </td>
      );
    });
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="task-manager">
      <div className="task-manager-header">
        <FilterHeader end={headerEnd}>
          <StaffFilterInput value={staffFilter} onChange={setStaffFilter} />
          {showPendingPayment ? (
            <PendingPaymentIndicator
              items={pendingPayments}
              activeCell={activeCell}
              onSelect={handlePendingSelect}
            />
          ) : null}
        </FilterHeader>
      </div>
      <div className="table-container">
        <table className="task-table">
          <thead>
            <tr>
              <th></th>
              <th>Name</th>
              <th>Turn</th>
              <th>T1</th>
              <th>T2</th>
              <th>T3</th>
              <th>T4</th>
              <th>T5</th>
              <th>T6</th>
              <th>T7</th>
              <th>T8</th>
              <th>T9</th>
              <th>T10</th>
              <th>T11</th>
              <th>T12</th>
              <th>T13</th>
              <th>T14</th>
              <th>T15</th>
              <th>T16</th>
              <th>T17</th>
              <th>T18</th>
              <th>T19</th>
              <th>T20</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan={23} className="task-manager-empty">
                  No staff match “{staffFilter.trim()}”
                </td>
              </tr>
            ) : (
              filteredRows.map(({ record, index }) => (
                <tr
                  key={record.id ?? index}
                  className={index === activeRow ? "row-active" : ""}
                >
                  <td className="avatar-cell">{renderStatusOrAvatar(record)}</td>
                  <td className="name-cell">{record.name}</td>
                  <td className="credits-cell">
                    <Chip
                      label={record.credits}
                      sx={{
                        fontSize: "16px",
                        fontWeight: "bold",
                        backgroundColor: "rgba(45, 191, 224, 0.36)",
                      }}
                    />
                  </td>
                  {renderServiceBoxes(record, index)}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

TaskManager.propTypes = {
  taskRecord: PropTypes.array.isRequired,
  onCellClick: PropTypes.func.isRequired,
  activeCell: PropTypes.object,
  activeName: PropTypes.string,
  headerEnd: PropTypes.node,
  showPendingPayment: PropTypes.bool,
};

export default TaskManager;
